import * as yup from 'yup';

import type { EvictorAdvancedEvictionConfigEntry } from '@cast/types-workload-eviction';

import {
  EVICTION_CONFIG_SETTING_KEYS,
  FIELD_NAMES,
  LABEL_OPERATORS,
  LABEL_VALUES_REQUIRED_ERROR_MESSAGE,
  MIN_REPLICAS,
  MIN_REPLICAS_ERROR_MESSAGE,
  SELECTOR_CRITERIA_REQUIRED_ERROR_MESSAGE,
} from './_constants';
import { FormValues } from './_types';
import {
  entryHasSelectorCriteria,
  isLabelValuesComplete,
} from './_utils/advancedConfig';
import { parseYamlToAdvancedConfig } from './_utils/parseYamlToAdvancedConfig';

const entryHasSetting = (entry: EvictorAdvancedEvictionConfigEntry): boolean =>
  EVICTION_CONFIG_SETTING_KEYS.some(
    (settingKey) => !!entry.settings?.[settingKey]
  );

const labelSelectorExpressionSchema = yup
  .object()
  .noUnknown()
  .shape({
    key: yup.string().required('key is required for each matchExpression'),
    operator: yup
      .string()
      .required('operator is required for each matchExpression')
      .oneOf(LABEL_OPERATORS, 'unsupported operator value'),
    values: yup.array().of(yup.string().defined()),
  })
  .test(
    'values-required-for-operator',
    LABEL_VALUES_REQUIRED_ERROR_MESSAGE,
    (expression) => {
      if (!expression?.key || !expression.operator) {
        return true;
      }

      return isLabelValuesComplete(expression);
    }
  );

const labelSelectorSchema = yup
  .object()
  .noUnknown()
  .shape({
    matchLabels: yup.object().default(undefined),
    matchExpressions: yup
      .array()
      .of(labelSelectorExpressionSchema)
      .default(undefined),
  })
  .default(undefined);

const podSelectorSchema = yup
  .object()
  .noUnknown()
  .shape({
    kind: yup.string(),
    namespace: yup.string(),
    replicasMin: yup
      .number()
      .integer()
      .min(MIN_REPLICAS, MIN_REPLICAS_ERROR_MESSAGE),
    labelSelector: labelSelectorSchema,
  })
  .default(undefined);

const nodeSelectorSchema = yup
  .object()
  .noUnknown()
  .shape({
    labelSelector: labelSelectorSchema,
  })
  .default(undefined);

const settingSchema = yup
  .object()
  .noUnknown()
  .shape({
    enabled: yup.boolean().required(),
  })
  .default(undefined);

const settingsSchema = yup.object().noUnknown().shape({
  disposable: settingSchema,
  removalDisabled: settingSchema,
  aggressive: settingSchema,
});

const evictionConfigEntrySchema = yup
  .object()
  .noUnknown()
  .shape({
    podSelector: podSelectorSchema,
    nodeSelector: nodeSelectorSchema,
    settings: settingsSchema,
  })
  .test(
    'entry-has-setting',
    'Each rule must enable a setting',
    (configEntry) => {
      if (!configEntry) {
        return true;
      }

      return entryHasSetting(configEntry);
    }
  )
  .test(
    'entry-has-criteria',
    SELECTOR_CRITERIA_REQUIRED_ERROR_MESSAGE,
    (configEntry) => {
      if (!configEntry) {
        return true;
      }

      return entryHasSelectorCriteria(configEntry);
    }
  );

export const advancedEvictionConfigSchema = yup
  .object()
  .noUnknown()
  .shape({
    evictionConfig: yup.array().of(evictionConfigEntrySchema),
  });

export const validationSchema = yup.object<FormValues>({
  [FIELD_NAMES.enabled]: yup.boolean(),
  [FIELD_NAMES.aggressiveMode]: yup.boolean(),
  [FIELD_NAMES.scopedMode]: yup.boolean(),
  [FIELD_NAMES.ignorePodDisruptionBudgets]: yup.boolean(),
  [FIELD_NAMES.nodeGracePeriodMinutes]: yup
    .number()
    .integer('Must be an integer')
    .nullable(),
  [FIELD_NAMES.cycleIntervalSeconds]: yup.number().nullable(),
  [FIELD_NAMES.maxTargetNodesPerCycle]: yup
    .number()
    .integer('Must be an integer')
    .nullable(),
  [FIELD_NAMES.advancedEvictorConfig]: yup
    .string()
    .test(
      'valid-eviction-config',
      'Invalid advanced configuration',
      function (value) {
        if (!value) {
          return true;
        }

        const result = parseYamlToAdvancedConfig(value);

        if (!result.success) {
          return this.createError({ message: result.error });
        }

        return true;
      }
    ),
});
